import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

/**
 * Reusable "Change password" card. Works for all authenticated users.
 * If the user signed up via Google (no password_hash), backend will return 400 —
 * we surface a friendly message.
 */
export default function ChangePasswordCard({ testPrefix = "profile", isGoogleOnly = false }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    if (current === next) return toast.error("New password must be different from current");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error("Failed to change password", { description: formatApiError(e) });
    } finally {
      setSaving(false);
    }
  };

  if (isGoogleOnly) {
    return (
      <div className="glass rounded-2xl p-6" data-testid={`${testPrefix}-change-password-card`}>
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="w-4 h-4 text-indigo-300" />
          <h3 className="font-display font-bold text-lg">Password</h3>
        </div>
        <p className="text-sm text-mute leading-relaxed">
          You signed up with Google. There's no password to change — just sign in with Google each time.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="glass rounded-2xl p-6" data-testid={`${testPrefix}-change-password-card`}>
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="w-4 h-4 text-indigo-300" />
        <h3 className="font-display font-bold text-lg">Change password</h3>
      </div>
      <div className="space-y-3">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">Current password</label>
          <input
            type="password" required autoComplete="current-password"
            value={current} onChange={(e) => setCurrent(e.target.value)}
            data-testid={`${testPrefix}-cp-current`}
            className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">New password (min 8)</label>
          <input
            type="password" required minLength={8} autoComplete="new-password"
            value={next} onChange={(e) => setNext(e.target.value)}
            data-testid={`${testPrefix}-cp-new`}
            className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-mute mb-1">Confirm new password</label>
          <input
            type="password" required minLength={6} autoComplete="new-password"
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
            data-testid={`${testPrefix}-cp-confirm`}
            className="w-full bg-black/50 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
          />
        </div>
      </div>
      <button
        type="submit" disabled={saving}
        data-testid={`${testPrefix}-cp-submit`}
        className="mt-5 bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 rounded-md px-4 py-2 text-sm font-medium"
      >
        {saving ? "Updating…" : "Update password"}
      </button>
    </form>
  );
}
